import {
  Grid,
  Typography,
  IconButton,
  Paper,
  TextField,
  Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";

import { useDispatch, useSelector } from "react-redux";
import { useNotesDisplay } from "./hooks";
import { NoteData } from "../../../store/api/devices/types";

import ConfirmDialog from "../../../components/Shared/Dialogs/ConfirmDialog";
import { DIALOGOPENFOR } from "../../../globals/globals";
import { setConfirmDialog } from "../../../store/helpers/helperReducers";
import { confirmDialogSelector } from "../../../store/helpers/helperSelectors";
import { isNoteSelector } from "../../../store/api/devices/selectors";
import { setSelectedNote } from "../../../store/api/devices/reducers";

import { useState, useEffect, useRef } from "react";
import AddNotes from "./AddNotes";
import { transfomDetail } from "../../../utils/helpers";

interface NotesProps {
  /** Allow automatic scrolling when notes change (e.g., after adding). */
  enableAutoScroll?: boolean;
}

const Notes: React.FC<NotesProps> = ({ enableAutoScroll = true }) => {
  const dispatch = useDispatch();

  const { data, handleDeleteNote, handleEditNote } = useNotesDisplay();

  const notesArray: NoteData[] = data ?? [];
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const selectedNoteId = useSelector(isNoteSelector);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    });
  };
  const scrollAndFocusInput = () => {
    requestAnimationFrame(() => {
      scrollToBottom();
      if (inputRef.current) {
        inputRef.current.focus({ preventScroll: true });
      }
    });
  };

  // ---- Edit State ----
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);

  const handleConfirmDelete = (actionName: string) => {
    handleDeleteNote(selectedNoteId, actionName);

    if (editingNoteId === selectedNoteId) {
      setIsEditing(false);
      setEditingNoteId(null);
      setEditValue("");
    }
  };

  const startEditing = (note: NoteData) => {
    setEditingNoteId(note.notesId);
    setEditValue(note.notes);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditingNoteId(null);
    setEditValue("");
  };

  const saveEdit = () => {
    if (!editingNoteId) return;
    handleEditNote(editingNoteId, editValue);
    setIsEditing(false);
    setEditingNoteId(null);
  };

  const handleDeleteClick = (noteId: number) => {
    dispatch(setSelectedNote(noteId));
    dispatch(setConfirmDialog(DIALOGOPENFOR.DELETENOTE));
  };

  useEffect(() => {
    if (!enableAutoScroll) return;
    scrollAndFocusInput();
  }, [enableAutoScroll, data?.length]);

  useEffect(() => {
    const handler = () => {
      scrollAndFocusInput();
    };

    window.addEventListener("focusNotesInput", handler);

    return () => window.removeEventListener("focusNotesInput", handler);
  }, []);


  return (
    <Grid
      item
      container
      direction="column"
      sx={{
        flexGrow: 1,
        overflowY: "auto",
        pr: 1,
        gap: 2,
      }}
      style={{ width: "100%", height: 300 }}
    >
      {/* Confirm Dialog */}
      {openConfirmDialog === DIALOGOPENFOR.DELETENOTE && (
        <ConfirmDialog
          actionName="delete"
          handleClickAction={handleConfirmDelete}
        >
          Are you sure you want to delete this note?
        </ConfirmDialog>
      )}

      {notesArray.map((noteItem) => {
        const isThisBeingEdited = isEditing && editingNoteId === noteItem.notesId;

        return (
          <Grid
            item
            key={noteItem.notesId}
            container
            direction="column"
            alignItems="flex-end"
          >
            <Grid
              item
              container
              alignItems="center"
              justifyContent="flex-end"
              flexWrap="wrap"
              sx={{ width: "100%" }}
              columnGap={1}
            >
              <Grid
                item
                sx={{ width: "100%", display: "flex", justifyContent: "flex-end" }}
              >
                {/* Edit icon for ALL items */}
                {!isThisBeingEdited && (
                  <IconButton size="small" sx={{ padding: '4px' }} onClick={() => startEditing(noteItem)}>
                    <ModeEditOutlineOutlinedIcon
                      sx={{ fontSize: 14, color: "var(--foreground)" }}
                    />
                  </IconButton>
                )}

                {isThisBeingEdited ? (
                  <Grid
                    container
                    direction="column"
                    sx={{
                      maxWidth: "100%",
                      background: "var(--muted)",
                      padding: "10px 14px",
                      borderRadius: "16px",
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      sx={{
                        width: "100%",
                        background: "var(--muted)",
                        borderRadius: "10px",
                        mb: 1.5,
                        '& .MuiInputBase-input': {
                          fontSize: '0.875rem',
                          padding: '8px 12px',
                          color: 'var(--foreground)',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'var(--border)',
                        },
                      }}
                    />

                    <Grid container justifyContent="flex-end" columnGap={1}>
                      <Button
                        size="small"
                        onClick={cancelEditing}
                        sx={{
                          backgroundColor: "var(--secondary)",
                          color: "var(--foreground)",
                          "&:hover": { backgroundColor: "var(--secondary)" },
                        }}
                      >
                        Cancel
                      </Button>

                      <Button
                        size="small"
                        onClick={saveEdit}
                        sx={{
                          backgroundColor: "var(--primary)",
                          color: "var(--primary-foreground)",
                          "&:hover": { backgroundColor: "var(--primary)" },
                        }}
                      >
                        Save
                      </Button>

                      <IconButton
                        size="small"
                        sx={{ padding: '4px' }}
                        onClick={() => handleDeleteClick(noteItem.notesId)}
                      >
                        <DeleteIcon sx={{ fontSize: 14 }} color="error" />
                      </IconButton>
                    </Grid>
                  </Grid>
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      width: "fit-content",
                      maxWidth: "65%",
                      background: "var(--muted)",
                      color: "var(--foreground)",
                      padding: "10px 14px",
                      borderRadius: "16px",
                    }}
                  >
                    <Typography sx={{
                      fontSize: "0.875rem", wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      whiteSpace: "pre-wrap",
                      color: "var(--foreground)",
                    }}>
                      {noteItem.notes}
                    </Typography>
                  </Paper>
                )}
              </Grid>
            </Grid>

            <Grid item sx={{ mt: 0.5, textAlign: "right" }}>
              <Typography variant="caption" sx={{ opacity: 0.7, color: "var(--muted-foreground)" }}>
                {new Date(noteItem.createdTS).toLocaleString(undefined, {
                  weekday: "long",
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })}{" "}
                {/* {transfomDetail(noteItem.lastUpdatedTS.toString())} - {noteItem.lastUpdatedTS} */}
                - {noteItem.createdBy}
              </Typography>
            </Grid>
          </Grid>
        );
      })}

      <AddNotes ref={inputRef} />
      <div ref={bottomRef} />
    </Grid>
  );
};

export default Notes;
